import { bbox } from "@turf/bbox";

import { LOCATION_SOURCES } from "stores/useCaseMapStore";

// The accumulator writes this key for machines whose events carried no
// coordinates, so it sits alongside real "lat,lng" keys and has to be skipped.
const NO_DATA_KEY = "No Data";

const LOCATION_FIELD_BY_SOURCE = {
  [LOCATION_SOURCES.WIFI]: "wifi_locations",
  [LOCATION_SOURCES.IP]: "ip_locations",
};

/**
 * Pick the position a machine should be pinned at for one location source.
 *
 * A machine's locations are a `{"lat,lng": event count}` map, and the ticket
 * asks for one pin per machine, so the most-observed coordinate wins.
 *
 * @returns {{lat: number, lng: number}|null} null when nothing is plottable.
 */
export const parseLocations = (locationMap = {}) => {
  let best = null;

  Object.entries(locationMap ?? {}).forEach(([key, count]) => {
    if (key === NO_DATA_KEY) return;

    const [lat, lng] = key.split(",").map(Number);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    if (!best || count > best.count) {
      best = { lat, lng, count };
    }
  });

  return best ? { lat: best.lat, lng: best.lng } : null;
};

/**
 * Split a case's machines into the ones plottable under `locationSource` and
 * the ones that have to be flagged "Not on map".
 */
export const splitByPlottability = (machines = [], locationSource) => {
  const field = LOCATION_FIELD_BY_SOURCE[locationSource];
  const pinned = [];
  const notOnMap = [];

  machines.forEach(machine => {
    const position = parseLocations(machine[field]);
    if (position) {
      pinned.push({ ...machine, position });
    } else {
      notOnMap.push(machine);
    }
  });

  return { pinned, notOnMap };
};

/**
 * Wrap pinned machines as a GeoJSON FeatureCollection for supercluster.
 *
 * The machine is nested under its own property key so its fields never collide
 * with the cluster metadata (cluster, point_count, ...) supercluster writes onto
 * a feature's properties.
 */
export const machinesToGeoJson = (pinned = []) => ({
  type: "FeatureCollection",
  features: pinned.map(machine => ({
    type: "Feature",
    id: machine.id,
    geometry: {
      type: "Point",
      coordinates: [machine.position.lng, machine.position.lat],
    },
    properties: { machine },
  })),
});

/**
 * The case's addresses that can be plotted. A case inherits its addresses from
 * the linked entity, and only geocoded ones carry coordinates.
 */
export const plottableAddresses = (addresses = []) =>
  addresses.filter(
    address =>
      Number.isFinite(address.latitude) && Number.isFinite(address.longitude)
  );

/**
 * The extent worth framing on load — the pinned machines plus any extra
 * positions, so the case's own address cannot start off-screen.
 *
 * Returns plain numbers rather than a google.maps.LatLngBounds so the extent
 * can be compared by value: the machines query hands back a fresh array on
 * every refetch, and a new bounds object with identical coordinates would
 * otherwise re-frame the map and discard the analyst's pan.
 *
 * @returns {[number, number, number, number]|null} [west, south, east, north],
 *   or null when there is nothing to frame.
 */
export const bboxFor = (geoJson, extraPositions = []) => {
  const points = [
    ...geoJson.features.map(feature => feature.geometry.coordinates),
    ...extraPositions.map(({ lat, lng }) => [lng, lat]),
  ];

  if (!points.length) {
    return null;
  }

  return bbox({ type: "MultiPoint", coordinates: points });
};

/** Render a `{value: count}` aggregate column as a short, readable string. */
export const formatCountMap = (countMap, limit = 2) => {
  const values = Object.keys(countMap ?? {}).filter(key => key !== NO_DATA_KEY);
  if (!values.length) return null;

  const shown = values.slice(0, limit).join(", ");
  const remaining = values.length - limit;

  return remaining > 0 ? `${shown} +${remaining}` : shown;
};

const easeInOutCubic = t =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// At most one flight per map. A second flyTo while the first is still running
// would interpolate from its own captured start, and the two would fight over
// the camera every frame.
const inFlight = new WeakMap();

/**
 * Animate the camera to a position, easing both the pan and the zoom.
 *
 * @returns {() => void} cancels the flight — call it from an effect cleanup so
 *   an unmount mid-animation stops touching the map.
 */
export function flyTo(map, { lat, lng, zoom = 15, duration = 800 }) {
  inFlight.get(map)?.();

  const fromCenter = map.getCenter();
  const fromLat = fromCenter.lat();
  const fromLng = fromCenter.lng();
  const fromZoom = map.getZoom();
  const start = performance.now();
  let frame;

  function step(now) {
    const t = Math.min((now - start) / duration, 1);
    const eased = easeInOutCubic(t);
    map.moveCamera({
      center: {
        lat: fromLat + (lat - fromLat) * eased,
        lng: fromLng + (lng - fromLng) * eased,
      },
      zoom: fromZoom + (zoom - fromZoom) * eased,
    });
    if (t < 1) {
      frame = requestAnimationFrame(step);
    } else {
      inFlight.delete(map);
    }
  }

  frame = requestAnimationFrame(step);

  const cancel = () => {
    cancelAnimationFrame(frame);
    inFlight.delete(map);
  };
  inFlight.set(map, cancel);

  return cancel;
}

const LAT_LONG_FIELD_BY_SOURCE = {
  [LOCATION_SOURCES.WIFI]: "wifi_lat_long",
  [LOCATION_SOURCES.IP]: "ip_lat_long",
};

/** Parse a case event's `"lat,lng"` string. */
export const parseLatLong = (latLong = "") => {
  const [lat, lng] = String(latLong).split(",").map(Number);

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

// The case-events table sorts by machine_id, so the events sharing a coordinate
// arrive in no useful order and have to be put in time order here.
const eventTime = event => new Date(event.server_timestamp ?? 0).getTime();
const byNewestFirst = (a, b) => eventTime(b) - eventTime(a);

/**
 * Wrap the plottable events as a FeatureCollection, one feature per distinct
 * coordinate.
 *
 * A machine reports from the same access point over and over, so hundreds of
 * events routinely share one coordinate and stack into a single marker that
 * looks like one event. Each position is therefore plotted once, carrying every
 * event at it, newest first, for the summary to step through.
 *
 * Events without a coordinate for the active source are dropped rather than
 * flagged — unlike machines, an event has no row of its own to be flagged on.
 */
export const eventsToGeoJson = (events = [], locationSource) => {
  const field = LAT_LONG_FIELD_BY_SOURCE[locationSource];
  const byPosition = new Map();

  events.forEach(event => {
    const position = parseLatLong(event[field]);
    if (!position) return;

    // Keyed on the parsed pair, not the raw string, so "40.7,-74" and
    // "40.70,-74.0" are recognized as the same place.
    const key = `${position.lat},${position.lng}`;
    const group = byPosition.get(key);

    if (group) {
      group.events.push(event);
      return;
    }

    byPosition.set(key, { position, events: [event] });
  });

  return {
    type: "FeatureCollection",
    features: Array.from(byPosition.values(), ({ position, events: stack }) => {
      stack.sort(byNewestFirst);

      return {
        type: "Feature",
        // The newest event identifies the stack: it is what the summary opens
        // on, and it survives a refetch that adds later events elsewhere.
        id: stack[0].id,
        geometry: {
          type: "Point",
          coordinates: [position.lng, position.lat],
        },
        properties: { events: stack, count: stack.length },
      };
    }),
  };
};

/**
 * Supercluster's own count abbreviation, applied to a count it did not produce.
 */
export const abbreviateCount = count => {
  if (count >= 10000) return `${Math.round(count / 1000)}k`;
  if (count >= 1000) return `${Math.round(count / 100) / 10}k`;

  return String(count);
};

export const arrToStrList = (data = []) => {
  if (!(data || []).length) {
    return "—";
  }
  return data.join(", ");
};

export const objToStrList = (data = {}) => {
  const arr = Object.keys(data || {});
  if (!arr.length) {
    return "—";
  }
  return arr.join(", ");
};
